<template>
  <!-- A sample DESTINATION page for the Layout Composer's Live Mobile preview.
       When a link in the mock header/nav is clicked, the phone shows one of these
       (placeholder data — a preview, not real records). Header/nav persist around
       it; "← Home" returns to the composed layout. -->
  <div class="cmpg">
    <div class="cmpg-bar">
      <button class="cmpg-back" @click.stop="$emit('home')">← Home</button>
      <span class="cmpg-crumb">Home › {{ title }}</span>
    </div>
    <div class="cmpg-body">
      <h2 class="cmpg-title">{{ title }}</h2>
      <div class="cmpg-sample">sample preview data</div>

      <!-- My Profile -->
      <table v-if="page === 'profile'" class="cmpg-kv">
        <tr><th>Name</th><td>Michael Mullins</td></tr>
        <tr><th>Email</th><td>mullring@jcsteele.com</td></tr>
        <tr><th>Company</th><td>JC Steele &amp; Sons (NC999)</td></tr>
        <tr><th>Phone</th><td>(800) 278-3353</td></tr>
        <tr><th>Role</th><td>Master</td></tr>
      </table>

      <!-- Order History -->
      <table v-else-if="page === 'orderHistory'" class="cmpg-tbl">
        <thead><tr><th>Order #</th><th>Date</th><th>PO</th><th>Total</th><th>Status</th></tr></thead>
        <tbody>
          <tr v-for="o in orders" :key="o.id">
            <td>{{ o.id }}</td><td>{{ o.date }}</td><td>{{ o.po }}</td>
            <td>{{ o.total }}</td><td><span class="cmpg-pill" :class="o.cls">{{ o.status }}</span></td>
          </tr>
        </tbody>
      </table>

      <!-- Saved Carts -->
      <ul v-else-if="page === 'savedCarts'" class="cmpg-list">
        <li v-for="c in savedCarts" :key="c.name">
          <b>{{ c.name }}</b><span>{{ c.items }} items · {{ c.total }}</span>
          <button class="cmpg-mini">Open</button>
        </li>
      </ul>

      <!-- Documents -->
      <ul v-else-if="page === 'documents'" class="cmpg-list">
        <li v-for="d in docs" :key="d.name">
          <b>📄 {{ d.name }}</b><span>{{ d.kind }} · {{ d.date }}</span>
          <button class="cmpg-mini">Download</button>
        </li>
      </ul>

      <!-- Search Parts — the shared 3-step build wizard (Company → Machine → Part) -->
      <composer-search-wizard v-else-if="page === 'search'" :sim="sim" />

      <!-- Start an Order / Request a Quote — a working search → add → cart flow -->
      <div v-else-if="page === 'order' || page === 'rfq'">
        <div class="cmpg-row">
          <input class="cmpg-input" v-model="q"
                 :placeholder="page === 'rfq' ? 'Search a part # to quote…' : 'Search a part # to add…'"
                 @input="onSearch" @keyup.enter="onSearch" />
          <button class="cmpg-btn" @click.stop="onSearch">Search</button>
        </div>
        <ul v-if="results.length" class="cmpg-results">
          <li v-for="r in results" :key="r.sku" @click.stop="pick(r)">
            <b>{{ r.sku }}</b> — {{ r.name }} <span class="cmpg-res-price">${{ r.price.toFixed(2) }}</span>
            <em class="cmpg-res-add">+ Add</em>
          </li>
        </ul>
        <div v-else-if="q && !results.length" class="cmpg-note">No matches for “{{ q }}”.</div>

        <h3 class="cmpg-sub">{{ page === 'rfq' ? 'Quote items' : 'Cart' }}</h3>
        <composer-cart :sim="sim" @place="placed = true" />
        <div v-if="placed" class="cmpg-setup-msg">{{ page === 'rfq' ? 'Quote requested ✓' : 'Order placed ✓' }}</div>
      </div>

      <!-- Account Setup — Master-only tools (simulated) -->
      <div v-else-if="page === 'accountSetup'" class="cmpg-setup">
        <p class="cmpg-note">Master-only — manage what's linked to this company. (Sample preview.)</p>
        <div class="cmpg-setup-grid">
          <div class="cmpg-stat"><b>6</b> Machines</div>
          <div class="cmpg-stat"><b>18</b> Assemblies</div>
          <div class="cmpg-stat"><b>142</b> Wear parts</div>
          <div class="cmpg-stat"><b>9</b> Users</div>
        </div>
        <h3 class="cmpg-sub">Copy setup from another company</h3>
        <div class="cmpg-row">
          <select class="cmpg-input" v-model="copyFrom">
            <option value="">— Choose a company —</option>
            <option v-for="c in setupCompanies" :key="c" :value="c">{{ c }}</option>
          </select>
          <button class="cmpg-btn" @click.stop="doCopy">Copy</button>
        </div>
        <div v-if="copyMsg" class="cmpg-setup-msg">{{ copyMsg }}</div>
        <h3 class="cmpg-sub">Setup gaps</h3>
        <ul class="cmpg-list">
          <li v-for="g in setupGaps" :key="g"><b>⚠ {{ g }}</b><button class="cmpg-mini">Fix now</button></li>
        </ul>
      </div>

      <!-- Signed out -->
      <div v-else-if="page === 'signOut'" class="cmpg-signout">
        <p>You’ve been signed out.</p>
        <button class="cmpg-btn" @click.stop="$emit('home')">Sign back in</button>
      </div>

      <!-- Privacy / Terms -->
      <p v-else-if="page === 'privacy' || page === 'terms'" class="cmpg-text">
        This is sample {{ title }} copy for the preview. The real page renders the
        published {{ title }} content.
      </p>

      <!-- fallback -->
      <p v-else class="cmpg-text">Sample content for “{{ title }}”.</p>
    </div>
  </div>
</template>

<script>
import { searchParts, addToCart, cartCount, cartTotal } from "@/assets/js/composerSampleParts";
import ComposerSearchWizard from "./ComposerSearchWizard.vue";
import ComposerCart from "./ComposerCart.vue";

export default {
  name: "ComposerMockPage",
  components: { ComposerSearchWizard, ComposerCart },
  props: {
    page: { type: String, required: true },
    sim: {
      type: Object,
      default: function () {
        return { cart: [] };
      },
    },
  },
  data() {
    return {
      q: "",
      results: [],
      placed: false,
      copyFrom: "",
      copyMsg: "",
      setupCompanies: ["Acme Brick (TX102)", "Belden Brick (OH410)", "General Shale (TN330)"],
      setupGaps: [
        "2 machines have no wear parts linked",
        "1 user has no machine access",
        "Assembly “Die” is missing 3 parts",
      ],
      orders: [
        { id: "SO-48217", date: "2026-05-28", po: "12345", total: "$3,257.40", status: "Shipped", cls: "ok" },
        { id: "SO-48104", date: "2026-05-11", po: "12290", total: "$840.80", status: "Preparing", cls: "warn" },
        { id: "SO-47990", date: "2026-04-22", po: "12188", total: "$2,938.26", status: "Complete", cls: "ok" },
      ],
      savedCarts: [
        { name: "Spiral liners — 90ADEX", items: 4, total: "$3,779.06" },
        { name: "Pug sealer refresh", items: 2, total: "$1,204.10" },
      ],
      docs: [
        { name: "90ADEX Operation Manual", kind: "Manual", date: "2025-11-02" },
        { name: "Invoice SO-48217", kind: "Invoice", date: "2026-05-28" },
        { name: "Wear-parts diagram", kind: "Drawing", date: "2025-08-14" },
      ],
    };
  },
  computed: {
    title() {
      return (
        {
          profile: "My Profile",
          orderHistory: "Order History",
          savedCarts: "Saved Carts",
          documents: "Documents",
          accountSetup: "Account Setup",
          search: "Search Parts",
          order: "Start an Order",
          rfq: "Request a Quote",
          signOut: "Sign Out",
          privacy: "Privacy",
          terms: "Terms",
        }[this.page] || this.page
      );
    },
    simCart() {
      return (this.sim && this.sim.cart) || [];
    },
    count() {
      return cartCount(this.sim);
    },
    total() {
      return cartTotal(this.sim);
    },
  },
  methods: {
    onSearch() {
      this.results = searchParts(this.q);
    },
    pick(part) {
      addToCart(this.sim, part);
      this.results = [];
      this.q = "";
      this.placed = false;
    },
    dec(idx) {
      var c = this.simCart;
      if (c[idx]) c[idx].qty = Math.max(1, c[idx].qty - 1);
    },
    doCopy() {
      if (!this.copyFrom) {
        this.copyMsg = "Choose a company to copy from.";
        return;
      }
      this.copyMsg = "Copied setup from " + this.copyFrom + " → this company (sample).";
    },
  },
  watch: {
    // Reset transient page state when the phone navigates to a different page.
    page() {
      this.q = "";
      this.results = [];
      this.placed = false;
      this.copyFrom = "";
      this.copyMsg = "";
    },
  },
};
</script>

<style scoped>
.cmpg {
  width: 100%;
  box-sizing: border-box;
  font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  color: #222;
}
.cmpg-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  background: #f3f5f7;
  border-bottom: 1px solid #e2e5e8;
}
.cmpg-back {
  border: 1px solid #cfd3d7;
  background: #fff;
  border-radius: 5px;
  padding: 4px 9px;
  cursor: pointer;
  font-weight: 600;
  color: #c8302a;
}
.cmpg-crumb {
  font-size: 12px;
  color: #777;
}
.cmpg-body {
  padding: 12px 12px 18px;
}
.cmpg-title {
  margin: 0 0 2px;
  font-size: 18px;
  color: #c8302a;
}
.cmpg-sample {
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #b6bbc0;
  margin-bottom: 10px;
}
.cmpg-kv {
  width: 100%;
  border-collapse: collapse;
}
.cmpg-kv th {
  text-align: left;
  width: 38%;
  color: #888;
  font-weight: 600;
  padding: 6px 6px 6px 0;
  vertical-align: top;
}
.cmpg-kv td {
  padding: 6px 0;
}
.cmpg-tbl {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.cmpg-tbl th {
  text-align: left;
  color: #888;
  border-bottom: 2px solid #eceff2;
  padding: 6px 4px;
}
.cmpg-tbl td {
  padding: 7px 4px;
  border-bottom: 1px solid #f1f1f1;
}
.cmpg-pill {
  border-radius: 10px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
}
.cmpg-pill.ok {
  background: #e7f6e7;
  color: #2a7d2a;
}
.cmpg-pill.warn {
  background: #fdf0e3;
  color: #b9711a;
}
.cmpg-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cmpg-list li {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 0;
  border-bottom: 1px solid #f1f1f1;
}
.cmpg-list li b {
  flex: 1 1 auto;
}
.cmpg-list li span {
  color: #888;
  font-size: 12px;
}
.cmpg-mini {
  border: 1px solid #c8302a;
  color: #c8302a;
  background: #fff;
  border-radius: 5px;
  padding: 4px 10px;
  cursor: pointer;
  font-weight: 600;
}
.cmpg-results {
  list-style: none;
  margin: 6px 0 0;
  padding: 0;
  border: 1px solid #e2e5e8;
  border-radius: 6px;
  overflow: hidden;
}
.cmpg-results li {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 9px;
  border-bottom: 1px solid #f1f1f1;
  cursor: pointer;
  font-size: 13px;
}
.cmpg-results li:hover {
  background: #fbe9e8;
}
.cmpg-res-price {
  margin-left: auto;
  color: #555;
}
.cmpg-res-add {
  color: #c8302a;
  font-weight: 700;
  font-style: normal;
}
.cmpg-sub {
  font-size: 13px;
  margin: 14px 0 6px;
  color: #444;
}
.cmpg-qty {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.cmpg-qty button {
  width: 22px;
  height: 22px;
  border: 1px solid #cfd3d7;
  background: #f6f7f8;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 700;
}
.cmpg-orderbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
  font-weight: 700;
}
.cmpg-setup-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 4px 0 8px;
}
.cmpg-stat {
  flex: 1 1 40%;
  background: #f6f7f8;
  border: 1px solid #e2e5e8;
  border-radius: 6px;
  padding: 8px 10px;
}
.cmpg-stat b {
  display: block;
  font-size: 18px;
  color: #c8302a;
}
.cmpg-setup-msg {
  margin-top: 8px;
  font-size: 12px;
  color: #2a7d2a;
  font-weight: 600;
}
/* Search wizard (Company → Machine → Part) */
.cmpg-steps {
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
}
.cmpg-stepdot {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #cfd3d7;
  background: #fff;
  color: #888;
  font-weight: 700;
  cursor: pointer;
}
.cmpg-stepdot.active {
  border-color: #c8302a;
  color: #c8302a;
}
.cmpg-stepdot.done {
  background: #c8302a;
  border-color: #c8302a;
  color: #fff;
}
.cmpg-step-h {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 15px;
  margin-bottom: 8px;
}
.cmpg-stepnum {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #888;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}
.cmpg-stepnum.ok {
  background: #2a7d2a;
}
.cmpg-pick {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cmpg-pick li {
  padding: 10px 12px;
  border: 1px solid #e2e5e8;
  border-radius: 6px;
  margin-bottom: 6px;
  cursor: pointer;
}
.cmpg-pick li span {
  color: #888;
}
.cmpg-pick li:hover {
  background: #fbe9e8;
}
.cmpg-pick li.sel {
  border-color: #c8302a;
  background: #fbe9e8;
  font-weight: 600;
}
.cmpg-row {
  display: flex;
  gap: 6px;
}
.cmpg-input {
  flex: 1 1 auto;
  min-width: 0;
  border: 1px solid #cfd3d7;
  border-radius: 5px;
  padding: 8px 9px;
}
.cmpg-btn {
  border: 0;
  border-radius: 5px;
  background: #c8302a;
  color: #fff;
  font-weight: 600;
  padding: 8px 14px;
  cursor: pointer;
}
.cmpg-note,
.cmpg-text {
  color: #555;
  font-size: 13px;
  line-height: 1.5;
  margin-top: 10px;
}
.cmpg-signout {
  text-align: center;
  padding: 18px 0;
}
.cmpg-signout p {
  margin-bottom: 12px;
  color: #555;
}
</style>
